/**
 * Backup Service
 *
 * Creates backups of existing IDE rule files before applying presets
 * Supports restoration and cleanup of backups
 */

import { promises as fs } from 'fs';
import { join, dirname, basename } from 'path';
import { IDE_FORMATS, type IdeFormat } from '../config/ide-formats.js';

export interface BackupMetadata {
    timestamp: string;
    preset?: string;
    version?: string;
    ides: string[];
    files: Array<{
        originalPath: string;
        backupPath: string;
        ide: string;
    }>;
}

export class BackupService {
    private backupDir = '.jai1/backups';

    /**
     * Create backup of existing rules for specified IDEs
     */
    async createBackup(ides: string[], presetSlug?: string): Promise<string | null> {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const backupPath = join(this.backupDir, timestamp);

        const backedUpFiles: Array<{
            originalPath: string;
            backupPath: string;
            ide: string;
        }> = [];

        let hasContent = false;

        for (const ideId of ides) {
            const format = IDE_FORMATS[ideId];
            if (!format) {
                console.warn(`Unknown IDE format: ${ideId}, skipping backup`);
                continue;
            }

            // Check if rules directory exists
            const rulesPath = format.rulesPath === '.' ? process.cwd() : join(process.cwd(), format.rulesPath);

            try {
                const exists = await this.pathExists(rulesPath);

                if (!exists) {
                    continue;
                }

                // For AGENTS.md and GEMINI.md, backup specific files
                if (ideId === 'agentsmd') {
                    await this.backupSingleFile('AGENTS.md', backupPath, ideId, backedUpFiles);
                    hasContent = true;
                } else if (ideId === 'gemini') {
                    await this.backupSingleFile('GEMINI.md', backupPath, ideId, backedUpFiles);
                    hasContent = true;
                } else {
                    // Backup entire directory
                    const stats = await fs.stat(rulesPath);
                    if (stats.isDirectory()) {
                        const files = await fs.readdir(rulesPath);

                        for (const file of files) {
                            if (file.endsWith(format.fileExtension)) {
                                const originalPath = join(rulesPath, file);
                                const relativePath = join(format.rulesPath, file);
                                const destPath = join(backupPath, ideId, file);

                                await fs.mkdir(dirname(destPath), { recursive: true });
                                await fs.copyFile(originalPath, destPath);

                                backedUpFiles.push({
                                    originalPath: relativePath,
                                    backupPath: join(ideId, file),
                                    ide: ideId,
                                });

                                hasContent = true;
                            }
                        }
                    }
                }
            } catch (error) {
                // Directory doesn't exist, skip
                continue;
            }
        }

        // If no files were backed up, don't create backup
        if (!hasContent) {
            return null;
        }

        // Create metadata file
        const metadata: BackupMetadata = {
            timestamp,
            preset: presetSlug,
            ides,
            files: backedUpFiles,
        };

        await fs.mkdir(backupPath, { recursive: true });
        await fs.writeFile(
            join(backupPath, 'metadata.json'),
            JSON.stringify(metadata, null, 2),
            'utf-8'
        );

        return backupPath;
    }

    /**
     * Backup a single file (for AGENTS.md, GEMINI.md)
     */
    private async backupSingleFile(
        filename: string,
        backupPath: string,
        ideId: string,
        backedUpFiles: Array<{ originalPath: string; backupPath: string; ide: string }>
    ): Promise<void> {
        const originalPath = join(process.cwd(), filename);

        try {
            const exists = await this.pathExists(originalPath);
            if (!exists) {
                return;
            }

            const destPath = join(backupPath, ideId, filename);
            await fs.mkdir(dirname(destPath), { recursive: true });
            await fs.copyFile(originalPath, destPath);

            backedUpFiles.push({
                originalPath: filename,
                backupPath: join(ideId, filename),
                ide: ideId,
            });
        } catch (error) {
            // File doesn't exist, skip
        }
    }

    /**
     * Restore files from a backup
     */
    async restoreBackup(backupPath: string): Promise<void> {
        const metadataPath = join(backupPath, 'metadata.json');
        const metadataContent = await fs.readFile(metadataPath, 'utf-8');
        const metadata: BackupMetadata = JSON.parse(metadataContent);

        console.log(`\nRestoring backup from ${metadata.timestamp}...`);

        for (const file of metadata.files) {
            const sourcePath = join(backupPath, file.backupPath);
            const destPath = join(process.cwd(), file.originalPath);

            await fs.mkdir(dirname(destPath), { recursive: true });
            await fs.copyFile(sourcePath, destPath);

            console.log(`✓ Restored ${file.originalPath}`);
        }

        console.log('\n✅ Backup restored successfully!');
    }

    /**
     * List all available backups
     */
    async listBackups(): Promise<BackupMetadata[]> {
        try {
            const backupDirPath = join(process.cwd(), this.backupDir);
            const exists = await this.pathExists(backupDirPath);

            if (!exists) {
                return [];
            }

            const entries = await fs.readdir(backupDirPath, { withFileTypes: true });
            const backups: BackupMetadata[] = [];

            for (const entry of entries) {
                if (entry.isDirectory()) {
                    const metadataPath = join(backupDirPath, entry.name, 'metadata.json');

                    try {
                        const metadataContent = await fs.readFile(metadataPath, 'utf-8');
                        const metadata: BackupMetadata = JSON.parse(metadataContent);
                        backups.push(metadata);
                    } catch {
                        // Skip invalid backups
                        continue;
                    }
                }
            }

            // Sort by timestamp (newest first)
            return backups.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
        } catch {
            return [];
        }
    }

    /**
     * Delete a specific backup
     */
    async deleteBackup(timestamp: string): Promise<void> {
        const backupPath = join(process.cwd(), this.backupDir, timestamp);
        await this.deleteDirectory(backupPath);
    }

    /**
     * Delete all backups older than specified days
     */
    async cleanupOldBackups(daysToKeep: number = 30): Promise<number> {
        const backups = await this.listBackups();
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

        let deletedCount = 0;

        for (const backup of backups) {
            const backupDate = new Date(backup.timestamp);

            if (backupDate < cutoffDate) {
                await this.deleteBackup(backup.timestamp);
                deletedCount++;
            }
        }

        return deletedCount;
    }

    /**
     * Get the most recent backup
     */
    async getLatestBackup(): Promise<BackupMetadata | null> {
        const backups = await this.listBackups();
        return backups.length > 0 ? backups[0] : null;
    }

    /**
     * Check if a path exists
     */
    private async pathExists(path: string): Promise<boolean> {
        try {
            await fs.access(path);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Recursively delete a directory
     */
    private async deleteDirectory(path: string): Promise<void> {
        try {
            const exists = await this.pathExists(path);
            if (!exists) {
                return;
            }

            const entries = await fs.readdir(path, { withFileTypes: true });

            for (const entry of entries) {
                const fullPath = join(path, entry.name);

                if (entry.isDirectory()) {
                    await this.deleteDirectory(fullPath);
                } else {
                    await fs.unlink(fullPath);
                }
            }

            await fs.rmdir(path);
        } catch (error) {
            // Ignore errors
        }
    }

    /**
     * Get backup directory path
     */
    getBackupDir(): string {
        return join(process.cwd(), this.backupDir);
    }

    /**
     * Ensure backup directory exists
     */
    async ensureBackupDir(): Promise<void> {
        const backupDirPath = join(process.cwd(), this.backupDir);
        await fs.mkdir(backupDirPath, { recursive: true });
    }
}
